import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import DeckWidget from './DeckWidget';
import {
  LayoutGrid, Table2, Settings, ChevronLeft, ChevronRight, LogOut, Sun, Moon, Monitor, BarChart3, Bot, Zap, Building2, Compass, User, Globe, Clock, ChevronUp, Inbox, Plus, X, Loader2, DollarSign, Users, Beaker, CreditCard, ShieldCheck, Lock, FileSearch, Crown, Palette, SlidersHorizontal
} from 'lucide-react';

const API = '/api';

// Common timezone list
const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "New York (EST/EDT)" },
  { value: "America/Chicago", label: "Chicago (CST/CDT)" },
  { value: "America/Denver", label: "Denver (MST/MDT)" },
  { value: "America/Los_Angeles", label: "Los Angeles (PST/PDT)" },
  { value: "America/Toronto", label: "Toronto (EST/EDT)" },
  { value: "America/Vancouver", label: "Vancouver (PST/PDT)" },
  { value: "America/Mexico_City", label: "Mexico City (CST/CDT)" },
  { value: "America/Sao_Paulo", label: "Sao Paulo (BRT)" },
  { value: "America/Buenos_Aires", label: "Buenos Aires (ART)" },
  { value: "America/Lima", label: "Lima (PET)" },
  { value: "America/Bogota", label: "Bogota (COT)" },
  { value: "America/Santiago", label: "Santiago (CLT)" },
  { value: "Pacific/Honolulu", label: "Honolulu (HST)" },
  { value: "America/Anchorage", label: "Anchorage (AKST)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Dublin", label: "Dublin (GMT/IST)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
  { value: "Europe/Amsterdam", label: "Amsterdam (CET/CEST)" },
  { value: "Europe/Madrid", label: "Madrid (CET/CEST)" },
  { value: "Europe/Rome", label: "Rome (CET/CEST)" },
  { value: "Europe/Zurich", label: "Zurich (CET/CEST)" },
  { value: "Europe/Stockholm", label: "Stockholm (CET/CEST)" },
  { value: "Europe/Helsinki", label: "Helsinki (EET/EEST)" },
  { value: "Europe/Athens", label: "Athens (EET/EEST)" },
  { value: "Europe/Warsaw", label: "Warsaw (CET/CEST)" },
  { value: "Europe/Moscow", label: "Moscow (MSK)" },
  { value: "Europe/Istanbul", label: "Istanbul (TRT)" },
  { value: "Europe/Lisbon", label: "Lisbon (WET/WEST)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Riyadh", label: "Riyadh (AST)" },
  { value: "Asia/Jerusalem", label: "Jerusalem (IST/IDT)" },
  { value: "Asia/Tehran", label: "Tehran (IRST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Dhaka", label: "Dhaka (BST)" },
  { value: "Asia/Bangkok", label: "Bangkok (ICT)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (HKT)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Asia/Seoul", label: "Seoul (KST)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Jakarta", label: "Jakarta (WIB)" },
  { value: "Asia/Manila", label: "Manila (PHT)" },
  { value: "Asia/Taipei", label: "Taipei (CST)" },
  { value: "Australia/Perth", label: "Perth (AWST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
  { value: "Australia/Melbourne", label: "Melbourne (AEST/AEDT)" },
  { value: "Australia/Brisbane", label: "Brisbane (AEST)" },
  { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)" },
  { value: "Africa/Cairo", label: "Cairo (EET)" },
  { value: "Africa/Johannesburg", label: "Johannesburg (SAST)" },
  { value: "Africa/Lagos", label: "Lagos (WAT)" },
  { value: "Africa/Nairobi", label: "Nairobi (EAT)" },
];

// Live clock component
const LiveClock = ({ timezone, className }) => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const update = () => {
      try {
        setTime(new Date().toLocaleTimeString('en-US', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: true }));
      } catch { setTime('--:--'); }
    };
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, [timezone]);
  return <span className={cn("font-mono", className)}>{time}</span>;
};

// Sidebar context
const SidebarContext = createContext(null);

// Floating Chat Context
const FloatingChatContext = createContext(null);

export const useFloatingChat = () => {
  const context = useContext(FloatingChatContext);
  return context || { isOpen: false, setIsOpen: () => {} };
};

export const FloatingChatProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <FloatingChatContext.Provider value={{ isOpen, setIsOpen }}>
      {children}
    </FloatingChatContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) throw new Error('useSidebar must be used within SidebarProvider');
  return context;
};

export const SidebarProvider = ({ children }) => {
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem('orka-sidebar-collapsed');
    return stored === 'true';
  });

  const toggle = () => {
    setCollapsed(prev => {
      const newVal = !prev;
      localStorage.setItem('orka-sidebar-collapsed', String(newVal));
      return newVal;
    });
  };

  return (
    <SidebarContext.Provider value={{ collapsed, toggle }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({ theme, setTheme }) => {
  const { collapsed, toggle } = useSidebar();
  const { user, logout } = useAuth();
  const { density, setDensity } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, setIsOpen } = useFloatingChat();

  // Profile settings state
  const [profileSettings, setProfileSettings] = useState(null);
  const [showAddTz, setShowAddTz] = useState(false);
  const [newTz, setNewTz] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [tzMismatch, setTzMismatch] = useState(null); // { browser, orka }
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isOrkaOwner, setIsOrkaOwner] = useState(false);
  // Phase 2 — module effective state map (PRD §9.5)
  const [moduleStates, setModuleStates] = useState({});  // module_key → effective state row
  const [accessResolveDialog, setAccessResolveDialog] = useState(null);  // {module, resolution}

  // Check Super Admin status (gates Testing Lab nav)
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/testing-lab/super-admin/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setIsSuperAdmin(!!d?.is_super_admin))
      .catch(() => setIsSuperAdmin(false));
  }, [user]);

  // Check Orka Owner status (gates Owner Console nav — PRD orka_owner_console v0.1 §10.2)
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/owner/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setIsOrkaOwner(!!d?.is_orka_owner))
      .catch(() => setIsOrkaOwner(false));
  }, [user]);

  // Phase 2 — fetch effective module state map (PRD §9.5)
  // Drives lock icons + access resolution dialogs on the sidebar.
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/core/modules/effective-state`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.items) return;
        const map = {};
        d.items.forEach((m) => { map[m.module_key] = m; });
        setModuleStates(map);
      })
      .catch(() => { /* fall back to permissive sidebar */ });
  }, [user]);

  // Fetch profile settings and detect timezone mismatch
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/auth/profile/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setProfileSettings(d);
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserTz && d.app_timezone !== browserTz) {
          setTzMismatch({ browser: browserTz, orka: d.app_timezone || 'UTC' });
        }
      })
      .catch(() => {});
  }, [user]);

  const acceptTimezoneChange = async () => {
    if (!tzMismatch) return;
    try {
      const res = await fetch(`${API}/auth/timezone-update`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ new_timezone: tzMismatch.browser, source: 'browser_detected' })
      });
      if (res.ok) {
        const result = await res.json();
        setProfileSettings(prev => ({ ...prev, app_timezone: tzMismatch.browser }));
        toast.success(`Timezone updated to ${tzMismatch.browser} (${result.floating_tasks_preserved} tasks preserved, ${result.absolute_events_rerendered} events re-rendered)`);
      }
    } catch { toast.error('Failed to update timezone'); }
    setTzMismatch(null);
  };

  const saveSettings = useCallback(async (updated) => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/auth/profile/settings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(updated)
      });
      if (res.ok) {
        setProfileSettings(await res.json());
        toast.success('Settings saved');
      }
    } catch { toast.error('Failed to save'); }
    finally { setSavingSettings(false); }
  }, []);

  const addSecondaryTz = () => {
    if (!newTz) return;
    const current = profileSettings?.secondary_timezones || [];
    if (current.includes(newTz)) { toast.error('Already added'); return; }
    const updated = { ...profileSettings, secondary_timezones: [...current, newTz] };
    setProfileSettings(updated);
    saveSettings(updated);
    setNewTz('');
    setShowAddTz(false);
  };

  const removeSecondaryTz = (tz) => {
    const current = profileSettings?.secondary_timezones || [];
    const updated = { ...profileSettings, secondary_timezones: current.filter(t => t !== tz) };
    setProfileSettings(updated);
    saveSettings(updated);
  };

  const updateAppTz = (v) => {
    const updated = { ...profileSettings, app_timezone: v };
    setProfileSettings(updated);
    saveSettings(updated);
  };

  const navItems = [
    { path: '/dashboard', label: 'Daily Dashboard', icon: LayoutGrid, module_key: 'dashboard' },
    { path: '/data', label: 'Tasks', icon: Table2, module_key: 'tasks' },
    { path: '/switchboard', label: 'Switchboard', icon: Inbox, module_key: 'switchboard' },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, module_key: 'analytics' },
    { path: '/big-picture', label: 'Big Picture', icon: Compass, module_key: 'big_picture' },
    { path: '/finance', label: 'Finance', icon: DollarSign, module_key: 'finance' },
    { path: '/ai', label: 'Orka AI', icon: Bot, module_key: 'ai' },
    { path: '/organisation', label: 'Organisation', icon: Building2, module_key: 'organisation' },
    { path: '/recruitment', label: 'Recruitment', icon: Users, module_key: 'recruitment' },
    ...(isSuperAdmin ? [{ path: '/testing-lab', label: 'Testing Lab', icon: Beaker, module_key: 'testing_lab' }] : []),
    ...(isSuperAdmin ? [{ path: '/admin/identity', label: 'Identity Admin', icon: ShieldCheck, module_key: 'admin_identity' }] : []),
    ...(isSuperAdmin ? [{ path: '/admin/audit', label: 'Architecture Audit', icon: FileSearch, module_key: 'admin_audit' }] : []),
    ...(isOrkaOwner ? [{ path: '/owner/overview', label: 'Owner Console', icon: Crown, module_key: 'owner_console' }] : []),
    { path: '/billing', label: 'Billing & Plan', icon: CreditCard, module_key: 'billing' },
    { path: '/settings', label: 'Settings', icon: Settings, module_key: 'settings' },
  ]
    // PRD §9.5: hide modules whose effective state is "hidden"
    .filter((item) => {
      const st = moduleStates[item.module_key];
      return !st || st.state !== 'hidden';
    });

  // PRD §9.7: when a locked module is clicked, fetch unlock path + open dialog
  const handleNavClick = async (e, item) => {
    const st = moduleStates[item.module_key];
    if (!st || (st.state !== 'locked' && st.state !== 'greyed_out')) return; // pass through
    e.preventDefault();
    try {
      const r = await fetch(`${API}/core/access/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ module_key: item.module_key }),
      });
      const resolution = r.ok ? await r.json() : null;
      setAccessResolveDialog({ module: { ...item, ...st }, resolution });
    } catch {
      navigate('/billing');
    }
  };

  const ThemeIcon = theme === 'dark' ? Sun : theme === 'light' ? Moon : Monitor;

  const secondaryTzs = profileSettings?.secondary_timezones || [];

  return (
    <>
    <aside
      className={cn(
        "fixed left-0 top-0 h-full bg-card border-r border-border z-40 flex flex-col orka-transition-colors duration-300",
        collapsed ? "w-16" : "w-56"
      )}
    >
      {/* Logo + AI Assistant Icon */}
      <div className={cn(
        "flex items-center h-16 px-4 border-b border-border",
        collapsed ? "justify-center" : "justify-between"
      )}>
        <div className={cn("flex items-center", collapsed ? "" : "gap-3")}>
          <img src="/logo.svg" alt="Orka" className="w-8 h-8 flex-shrink-0" />
          {!collapsed && <span className="text-xl font-semibold tracking-tight">Orka</span>}
        </div>
        {!collapsed && (
          <Button
            variant="ghost" size="icon"
            className={cn("relative group orka-transition-colors", isOpen && "bg-primary/10")}
            onClick={() => setIsOpen(!isOpen)}
            data-testid="floating-ai-toggle"
          >
            <div className={cn(
              "w-7 h-7 rounded-full flex items-center justify-center orka-transition-colors",
              isOpen
                ? "bg-gradient-to-br from-violet-500 to-indigo-600 shadow-lg shadow-violet-500/30"
                : "bg-gradient-to-br from-slate-400 to-slate-500 group-hover:from-violet-500 group-hover:to-indigo-600"
            )}>
              <Zap className="h-4 w-4 text-white" />
            </div>
            {!isOpen && <span className="absolute inset-0 rounded-full bg-violet-500/20 animate-ping opacity-0 group-hover:opacity-100" />}
          </Button>
        )}
      </div>

      {/* The Deck */}
      <div className="px-2 py-2 border-b border-border">
        <DeckWidget collapsed={collapsed} />
      </div>

      {/* Navigation - proper <a> links for right-click support */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const st = moduleStates[item.module_key];
          const locked = st && (st.state === 'locked' || st.state === 'greyed_out');
          const limited = st && st.state === 'limited';
          return (
            <a
              key={item.path}
              href={item.path}
              onClick={(e) => {
                if (locked) {
                  handleNavClick(e, item);
                  return;
                }
                e.preventDefault();
                navigate(item.path);
              }}
              className={cn(
                "flex items-center w-full rounded-md text-sm font-medium transition-colors h-9",
                collapsed ? "px-2 justify-center" : "px-3",
                isActive
                  ? "bg-secondary text-secondary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                locked && "opacity-60",
              )}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              data-module-state={st?.state || 'enabled'}
            >
              <item.icon className={cn("h-5 w-5 flex-shrink-0", collapsed ? "" : "mr-3")} />
              {!collapsed && (
                <span className="flex items-center gap-1.5 flex-1">
                  {item.label}
                  {locked && <Lock className="h-3 w-3 text-amber-500 ml-auto" data-testid={`nav-lock-${item.module_key}`} />}
                  {limited && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 ml-auto">limited</span>}
                </span>
              )}
            </a>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="p-2 border-t border-border space-y-2">
        {/* Appearance (theme + density) */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={cn('w-full justify-start', collapsed ? 'px-2' : 'px-3')}
              data-testid="appearance-toggle"
            >
              <ThemeIcon className={cn('h-5 w-5', collapsed ? '' : 'mr-3')} />
              {!collapsed && (
                <span className="flex-1 text-left flex items-center gap-1.5">
                  Appearance
                  <span className="text-[10px] text-muted-foreground font-normal capitalize ml-auto">
                    {theme === 'system' ? 'auto' : theme} · {density}
                  </span>
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent side="right" align="end" className="w-64 p-3" data-testid="appearance-popover">
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                  <Palette className="w-3 h-3" /> Theme
                </div>
                <div className="grid grid-cols-3 gap-1" data-testid="appearance-theme-group">
                  {[
                    { key: 'light', label: 'Light', Icon: Sun },
                    { key: 'dark', label: 'Dark', Icon: Moon },
                    { key: 'system', label: 'System', Icon: Monitor },
                  ].map(({ key, label, Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTheme(key)}
                      className={cn(
                        'flex flex-col items-center gap-1 py-2 rounded-lg border text-[11px] font-medium transition-colors',
                        theme === key
                          ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-200'
                          : 'border-border hover:bg-accent text-muted-foreground',
                      )}
                      data-testid={`appearance-theme-${key}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                  <SlidersHorizontal className="w-3 h-3" /> Density
                </div>
                <div className="grid grid-cols-3 gap-1" data-testid="appearance-density-group">
                  {[
                    { key: 'compact', label: 'Compact', hint: 'Dense' },
                    { key: 'standard', label: 'Standard', hint: 'Balanced' },
                    { key: 'calm', label: 'Calm', hint: 'Roomy' },
                  ].map(({ key, label, hint }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setDensity(key)}
                      className={cn(
                        'flex flex-col items-center gap-0.5 py-2 rounded-lg border text-[11px] font-medium transition-colors',
                        density === key
                          ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-200'
                          : 'border-border hover:bg-accent text-muted-foreground',
                      )}
                      data-testid={`appearance-density-${key}`}
                    >
                      <span>{label}</span>
                      <span className="text-[9px] opacity-70">{hint}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">
                  Calm swaps the full-bleed task header for a surface-bg card with project edge.
                </p>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* User Profile - always visible when user exists */}
        {user && (
          <Popover>
            <PopoverTrigger asChild>
              <button className={cn(
                "w-full flex items-center gap-2 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer text-left",
                collapsed ? "justify-center" : ""
              )} data-testid="sidebar-user-profile">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full flex-shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4" />
                  </div>
                )}
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                )}
                {!collapsed && <ChevronUp className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="w-80 p-0" data-testid="profile-popover">
              {/* Profile header */}
              <div className="p-4 border-b">
                <div className="flex items-center gap-3">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                </div>
              </div>

              {/* Timezone section */}
              <div className="p-3 space-y-2.5 border-b">
                {/* App timezone */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Your Timezone</span>
                  </div>
                  <Select value={profileSettings?.app_timezone || 'UTC'} onValueChange={updateAppTz}>
                    <SelectTrigger className="h-8 text-xs" data-testid="sidebar-app-tz-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[250px]">
                      {TIMEZONE_OPTIONS.map(tz => (
                        <SelectItem key={tz.value} value={tz.value} className="text-xs">{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Primary clock */}
                <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded-md">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs">{profileSettings?.app_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
                  <LiveClock timezone={profileSettings?.app_timezone || 'UTC'} className="text-xs ml-auto font-medium" />
                </div>

                {/* Secondary timezones */}
                {secondaryTzs.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Team Timezones</span>
                    {secondaryTzs.map(tz => (
                      <div key={tz} className="flex items-center gap-2 px-2 py-1.5 bg-muted/30 rounded-md group">
                        <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs truncate flex-1">{TIMEZONE_OPTIONS.find(o => o.value === tz)?.label || tz}</span>
                        <LiveClock timezone={tz} className="text-xs font-medium" />
                        <button
                          onClick={() => removeSecondaryTz(tz)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-tz-${tz.replace(/\//g, '-')}`}
                        >
                          <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add timezone */}
                {showAddTz ? (
                  <div className="flex items-center gap-1.5">
                    <Select value={newTz} onValueChange={setNewTz}>
                      <SelectTrigger className="h-7 text-xs flex-1" data-testid="add-tz-select">
                        <SelectValue placeholder="Select timezone..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {TIMEZONE_OPTIONS.filter(tz => tz.value !== (profileSettings?.app_timezone || 'UTC') && !secondaryTzs.includes(tz.value)).map(tz => (
                          <SelectItem key={tz.value} value={tz.value} className="text-xs">{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-7 px-2 text-xs" onClick={addSecondaryTz} disabled={!newTz || savingSettings}>
                      {savingSettings ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => { setShowAddTz(false); setNewTz(''); }}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddTz(true)}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full px-2 py-1"
                    data-testid="add-team-tz-btn"
                  >
                    <Plus className="w-3 h-3" />
                    Add team timezone
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="p-2 space-y-1">
                <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-8" onClick={() => navigate('/settings')} data-testid="profile-settings-link">
                  <Settings className="w-3.5 h-3.5 mr-2" />Settings
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-8 text-destructive" onClick={logout} data-testid="profile-logout">
                  <LogOut className="w-3.5 h-3.5 mr-2" />Sign Out
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Collapse toggle */}
        <Button
          variant="ghost"
          className={cn("w-full justify-start", collapsed ? "px-2" : "px-3")}
          onClick={toggle}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5 mr-3" />
              <span>Collapse</span>
            </>
          )}
        </Button>
      </div>
    </aside>

    {/* Timezone mismatch popup */}
    {tzMismatch && (
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-card border border-border shadow-xl rounded-xl p-4 max-w-md" data-testid="tz-mismatch-popup">
        <div className="text-sm font-semibold text-foreground mb-2">Timezone Change Detected</div>
        <p className="text-xs text-muted-foreground mb-3">
          Your device timezone is <strong>{tzMismatch.browser}</strong>, but Orka is set to <strong>{tzMismatch.orka}</strong>.
          Would you like to switch?
        </p>
        <p className="text-[10px] text-muted-foreground mb-3">
          Your manual tasks will keep the same wall-clock time. Calendar meetings will shift to the correct local time.
        </p>
        <div className="flex gap-2 justify-end">
          <button onClick={() => setTzMismatch(null)} className="text-xs px-3 py-1.5 rounded-md border border-border hover:bg-accent transition-colors">
            Keep {tzMismatch.orka}
          </button>
          <button onClick={acceptTimezoneChange} className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/80 transition-colors">
            Switch to {tzMismatch.browser}
          </button>
        </div>
      </div>
    )}

    {/* Phase 2 — Access Resolution dialog (PRD §9.7) */}
    {accessResolveDialog && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        data-testid="access-resolution-dialog"
        onClick={() => setAccessResolveDialog(null)}
      >
        <div
          className="bg-card border border-border shadow-2xl rounded-xl p-6 max-w-md w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-3 mb-3">
            <Lock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-base">{accessResolveDialog.module.label} is locked</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {accessResolveDialog.resolution?.plain_english_reason
                  || accessResolveDialog.module.plain_english_reason
                  || 'This feature is not available on your current plan.'}
              </p>
            </div>
            <button
              onClick={() => setAccessResolveDialog(null)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="access-dialog-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {(accessResolveDialog.resolution?.next_steps || []).length > 0 && (
            <div className="space-y-2 mt-4 border-t pt-4">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recommended next step</div>
              {accessResolveDialog.resolution.next_steps.map((step, idx) => (
                <button
                  key={idx}
                  data-testid={`access-step-${step.type}`}
                  onClick={() => {
                    setAccessResolveDialog(null);
                    if (step.target_url) navigate(step.target_url);
                  }}
                  className="w-full text-left p-3 rounded-md border border-border hover:bg-accent transition-colors flex items-center justify-between gap-2"
                >
                  <div>
                    <div className="text-sm font-medium">{step.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{step.description}</div>
                  </div>
                  {step.target_url && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export const PageLayout = ({ children, title, actions, theme, setTheme, hideHeader = false, chromeless = false }) => {
  const { collapsed } = useSidebar();

  return (
    <div className="min-h-screen bg-background">
      <Sidebar theme={theme} setTheme={setTheme} />
      
      <div className={cn(
        "orka-transition-colors duration-300",
        collapsed ? "ml-16" : "ml-56"
      )}>
        {!hideHeader && (
          /* Page header */
          <header className="h-16 bg-card border-b border-border px-6 flex items-center justify-between">
            <h1 className="text-xl font-semibold">{title}</h1>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </header>
        )}
        
        {/* Page content */}
        <main className={chromeless ? '' : 'p-6'}>
          {children}
        </main>
      </div>
    </div>
  );
};
